import { BufferGeometry, CatmullRomCurve3, Mesh, MeshBasicMaterial, TubeGeometry, Vector3 } from 'three';
import { InteractiveSplineToolEnum } from '@/modules/three/common/curve/interactiveSpline/InteractiveSplineToolEnum';
import { CircularLinkedList } from '@common/CircularLinkedList.ts';
import DrawingHandler from '@/modules/three/common/curve/interactiveSpline/DrawingHandler.js';
import DraggingHandler from '@/modules/three/common/curve/interactiveSpline/DraggingHandler.js';
import { getSelectable, Selectable } from '@decorators/Selectable';
import { threeInteractionStore } from '@modules/three/store/threeInteractionStore';
import SplineControlPoint from './SplineControlPoint';
import { EventHandler } from './BasePointerHandler';
import { Mesh as ExtendedMesh } from '@modules/three/common/mesh';
import { decimateOrderedLoop, dedupeOrderedLoopConsecutive } from '@modules/three/common/curve/orderCutSegmentsIntoLoop';
import { SEGMENTATION_VISUAL } from '@modules/three/common/visual/segmentationVisuals';
import concaveman from 'concaveman';

const TUBE_SEGMENTS = 64;
const TUBE_RADIUS = SEGMENTATION_VISUAL.splineTubeRadius;
const TUBE_RADIUS_SEGMENTS = 10;
const CLOSED = true;

const LINE_MATERIAL = new MeshBasicMaterial({
  color: SEGMENTATION_VISUAL.splineTube,
  transparent: true,
  opacity: SEGMENTATION_VISUAL.splineTubeOpacity,
  depthWrite: false,
});

export interface CreateLineOptions {
  /** Points already form a closed contour loop (skip concave-hull reordering). */
  preordered?: boolean;
  /** When set with `preordered`, decimate to this minimum spacing between control points. */
  minSpacing?: number;
  /** Keep every point after ordering instead of thinning the control set. */
  preserveAllPoints?: boolean;
  /** Thin the ordered loop down to roughly this many evenly-spaced control points. */
  targetControlPoints?: number;
}

@Selectable
export default class InteractiveSpline extends ExtendedMesh {
  public linePoints: CircularLinkedList<SplineControlPoint>;
  public parentMesh: Mesh;
  private curve: CatmullRomCurve3 | null = null;
  readonly thresholdFactor = 8;

  private currentMode: InteractiveSplineToolEnum = InteractiveSplineToolEnum.Dragging;
  private eventHandler: EventHandler;
  private boundKeyDown = this.onKeyDown.bind(this);

  

  private canInteract = true;

  constructor(points: Vector3[], parentMesh: Mesh, options?: CreateLineOptions) {
    super(new BufferGeometry(), LINE_MATERIAL);
    this.linePoints = new CircularLinkedList<SplineControlPoint>();
    this.parentMesh = parentMesh;

    this.createLine(points, options);
    this.eventHandler = new DraggingHandler(this);
    document.addEventListener('keydown', this.boundKeyDown);
    threeInteractionStore.subscribe((state) => this.setMode(state.editableLineTool));
  }


  /**
   * Creates the line geometry from the circular linked list of points.
   */
  public createLine(points?: Vector3[], options?: CreateLineOptions): void {
    if (points) {
      if (points.length < 2) {
        return;
      }

      this.geometry.dispose();
      this.linePoints.clear();

      const controlPoints = options?.preordered
        ? options.preserveAllPoints
          ? dedupeOrderedLoopConsecutive(points)
          : decimateOrderedLoop(points, options.minSpacing ?? this.estimateLoopSpacing(points))
        : this.buildControlPointsFromCloud(points, options);

      controlPoints.forEach((point, index) => {
        this.addSplineObject(point, index);
      });
    }

    this.reindexControlPoints();
    this.rebuildLineFromControlPoints();
  }

  private buildControlPointsFromCloud(points: Vector3[], options?: CreateLineOptions): Vector3[] {
    const sortedPoints = this.sortPointsForDentalArch(points);
    if (options?.preserveAllPoints) {
      return dedupeOrderedLoopConsecutive(sortedPoints);
    }

    const spacing = this.spacingForTargetCount(sortedPoints, options?.targetControlPoints);
    const decimated = decimateOrderedLoop(sortedPoints, spacing);
    return decimated.length >= 2 ? decimated : sortedPoints.slice(0, Math.min(sortedPoints.length, 2));
  }

  /** Loop spacing that yields ~`target` evenly-spaced control points (defaults to a manageable arch count). */
  private spacingForTargetCount(points: Vector3[], target?: number): number {
    if (points.length < 2) return 0.5;
    let perimeter = 0;
    for (let i = 0; i < points.length; i++) {
      perimeter += points[i].distanceTo(points[(i + 1) % points.length]);
    }
    const count = Math.max(8, Math.min(points.length, target ?? 28));
    return Math.max(perimeter / count, 1e-4);
  }

  private estimateLoopSpacing(points: Vector3[]): number {
    if (points.length < 2) return 0.5;
    let perimeter = 0;
    for (let i = 0; i < points.length; i++) {
      perimeter += points[i].distanceTo(points[(i + 1) % points.length]);
    }
    const targetCount = Math.max(8, Math.min(36, Math.round(points.length / 5)));
    return Math.max(perimeter / targetCount, 1e-4);
  }

  /** Keeps stored control-point indices aligned with circular-list positions. */
  public reindexControlPoints(): void {
    this.linePoints.forEach((point, index) => {
      point.setIndexInSpline(index);
    });
  }

  private rebuildLineFromControlPoints(): void {
    const positionsArray: Vector3[] = [];
    this.linePoints.forEach((node) => positionsArray.push(node.position.clone()));
    if (positionsArray.length < 2) {
      return;
    }
    this.curve = new CatmullRomCurve3(positionsArray, CLOSED);
    const tubeSegments = Math.max(TUBE_SEGMENTS, positionsArray.length * 2);
    this.geometry = new TubeGeometry(this.curve, tubeSegments, TUBE_RADIUS, TUBE_RADIUS_SEGMENTS, CLOSED);
  }

  /**
   * Sorts a cloud of points into a continuous dental arch loop using a 2D concave hull.
   * Projects points to XZ plane, calculates hull, and maps back to 3D.
   */
  private sortPointsForDentalArch(points: Vector3[]): Vector3[] {
    if (points.length < 3) return points;

    // 1. Project to 2D (XZ plane)
    // We assume Y is the vertical axis (insertion direction)
    const points2D: [number, number][] = points.map(p => [p.x, p.z]);

    // 2. Calculate Concave Hull
    // concavity=2 is standard. Lower (e.g. 1.5) follows shape tighter, Higher (3+) makes it more convex.
    // 2.0 usually works well for dental arches.
    const polygon = concaveman(points2D, 2.0);

    // 3. Map back to closest original 3D points
    const sortedPoints: Vector3[] = [];
    
    // Concaveman returns a closed loop (first point == last point).
    // We exclude the duplicate last point because InteractiveSpline handles closure via the 'closed' parameter.
    const len = polygon.length;
    const isClosed = len > 0 && 
      polygon[0][0] === polygon[len - 1][0] && 
      polygon[0][1] === polygon[len - 1][1];
    const count = isClosed ? len - 1 : len;

    for (let i = 0; i < count; i++) {
      const [x, z] = polygon[i];
      
      // Find the closest original 3D point to this 2D hull point
      // (Simple nearest-neighbor search is efficient enough for <1000 points)
      let closest = points[0];
      let minDst = Infinity;
      
      for (const p of points) {
        const d = (p.x - x) ** 2 + (p.z - z) ** 2;
        if (d < minDst) {
          minDst = d;
          closest = p;
        }
      }
      sortedPoints.push(closest);
    }

    return sortedPoints;
  }

  public addSplineObject(position: Vector3, indexInSpline: number): Mesh {
    const mesh = this.createSplineControlPoint(position, indexInSpline);
    this.linePoints.add(mesh);
    return mesh;
  }

  public createSplineControlPoint(position: Vector3, indexInSpline: number) {
    return new SplineControlPoint(position, SEGMENTATION_VISUAL.controlPointCoreRadius, this, indexInSpline);
  }

  public indexOfControlPoint(point: SplineControlPoint): number {
    return this.linePoints.indexOf(point);
  }


  public updateLine(): void {
    this.geometry.dispose();
    this.rebuildLineFromControlPoints();
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.key.toLowerCase() === 'm') {
      if (this.currentMode === InteractiveSplineToolEnum.Drawing) {
        this.setMode(InteractiveSplineToolEnum.Dragging);
      } else {
        this.setMode(InteractiveSplineToolEnum.Drawing);
      }
    }
  }

  public setMode(mode: InteractiveSplineToolEnum): void {
    this.eventHandler.dispose();
    this.currentMode = mode;
    if (mode === InteractiveSplineToolEnum.Drawing) {
      this.eventHandler = new DrawingHandler(this);
    } else {
      this.eventHandler = new DraggingHandler(this);
    }
  }

  public onPointerDown(iselectedPoint: SplineControlPoint): void {
    if (!this.canInteract) return;
    this.eventHandler.onPointerDown(iselectedPoint);
  }

  public dispose(): void {
    if (this.eventHandler && this.eventHandler.dispose) {
      this.eventHandler.dispose();
    }
    document.removeEventListener('keydown', this.boundKeyDown);
    this.linePoints.clear();
    super.dispose();
  }

  public showAll(): void {
    getSelectable(this).showAll((i) => i.setVisible(true));
  }

  public hideOthers(): void {
    getSelectable(this).hideOthers((i) => i.setVisible(false));
  }

  public isAnySelected(): boolean {
    return getSelectable(this).isAnySelected();
  }

  public getCurve(): CatmullRomCurve3 | null {
    return this.curve;
  }

  public getControlPoints(): Vector3[] {
    const points: Vector3[] = [];
    this.linePoints.forEach((point) => points.push(point.position.clone()));
    return points;
  }

  public setCanInteract(canInteract: boolean): void {
    this.canInteract = canInteract;
    this.linePoints.forEach((point) => point.setVisible(canInteract));
  }

  public getOtherInstances(): this[] {
    return getSelectable(this).getOtherInstances?.() ?? [];
  }

  public setVisible(visible: boolean): void {
    this.linePoints.forEach((point) => {
      point.setVisible(visible);
    });
    super.setVisible(visible);
  }

  public getClosestSplineControlPoint(point: Vector3): SplineControlPoint | null {
    let closestPoint: SplineControlPoint | null = null;
    let closestDistance = Infinity;
    this.linePoints.forEach((splinePoint) => {
      const distance = splinePoint.position.distanceTo(point);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = splinePoint;
      }
    });
    return closestPoint;
  }
}

